import React, { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from "@/hooks/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Upload } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { BASE_URL } from '@/config/api';

interface ImportStudentsModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (data: any) => void; // Ajout de la prop onImport
}

const ImportStudentsModal = ({ open, onClose, onImport }: ImportStudentsModalProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const isMobile = useIsMobile();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    // Check if user exists and has an id to use as school_id
    if (!file || !user?.school_id) {
      toast({
        title: t('students.import.error'),
        description: 'Missing file or user information',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('schoolId', user.school_id); // Using user.id as school_id

    try {
      const response = await fetch(`${BASE_URL}/api/student/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Import failed');
      }

      const data = await response.json();
      console.log('Import successful:', data);

      onImport(data); // Appeler la fonction de rappel avec les données importées

      toast({
        title: t('students.import.success'),
        description: t('students.import.successDescription'),
      });
      onClose();
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: t('students.import.error'),
        description: t('students.import.errorDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      setFile(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
        <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            {t('students.import.title')}
          </DialogTitle>
        </DialogHeader>


      <ScrollArea className={isMobile ? "max-h-[calc(100dvh-180px)]" : "max-h-[60vh]"}>
      <div className={`grid gap-4 py-4 ${isMobile ? "px-6" : ""}`}>
        <div className="space-y-6 mt-3">
          <div className="space-y-4">
            <div className="p-6 border-2 border-dashed rounded-lg bg-muted/50">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {t('students.import.description')}
                </p>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-muted-foreground
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-primary file:text-primary-foreground
                    hover:file:bg-primary/90
                    cursor-pointer"
                />
              </div>
            </div>

            {file && (
              <p className="text-sm text-muted-foreground">
                {t('students.import.selectedFile')}: {file.name}
              </p>
            )}
          </div>

        </div>
        </div>
        </ScrollArea>
        <DialogFooter className={isMobile ? "px-6 py-4 border-t sticky bottom-0 bg-gray-100 z-20" : "pt-5"}>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!file || isUploading}
            >
              {isUploading ? t('common.processing') : t('common.done')}
            </Button>
          </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportStudentsModal;
